import { useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { isNativeApp, setNativeMeetingSchedule } from 'common/webview';
import { AttendancePrompt } from 'components/meeting/AttendancePrompt';
import { MeetingRepository } from 'repository/meetingRepository';
import { instance } from 'common/axios/axios';
import { getApiUrl } from 'common/axios/getApiUrl';
import { IcParty, IcPhone } from 'components/common/icons/EmojiIcons';
import { AppScreen, Button } from 'components/ds';
import styles from './InvitationResult.module.css';

/**
 * 참여 완료 페이지
 * - 앱 안: 모임 상세로 바로 이동
 * - 브라우저: 앱 설치 유도
 */
export const InvitationResult = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { inviteCode: rawInviteCode } = useParams();
    const inviteCode = rawInviteCode?.toUpperCase();
    const location = useLocation();
    const meeting = location.state?.meeting;
    const meetingId = location.state?.meetingId;

    const inApp = isNativeApp();
    const [showAttendance, setShowAttendance] = useState(true);

    const handleAttendanceSubmit = async (status: 'ATTENDING' | 'NOT_ATTENDING' | 'UNDECIDED', locationSharing: boolean) => {
        if (meetingId) {
            // 게스트인 경우 → 게스트 토큰으로 공개 엔드포인트 호출 (인증 불필요)
            const guestToken = sessionStorage.getItem('guestToken');
            const guestMeetingId = sessionStorage.getItem('guestMeetingId');
            const isGuest = !!(guestToken && guestMeetingId === meetingId);

            try {
                if (isGuest) {
                    const res: any = await instance.put(
                        `${getApiUrl('meetings')}/${meetingId}/members/guest`,
                        { guestToken, attendanceStatus: status, locationSharing }
                    );
                    if (res?.status >= 200 && res?.status < 300) {
                        localStorage.setItem(`locationSharing_${meetingId}`, String(locationSharing));
                    }
                } else {
                    await MeetingRepository.onPostJoinMeetingByCode(inviteCode || '');
                    const res: any = await MeetingRepository.onPutMyMembership(meetingId, { attendanceStatus: status, locationSharing });
                    if (res?.status >= 200 && res?.status < 300) {
                        localStorage.setItem(`locationSharing_${meetingId}`, String(locationSharing));
                    }
                }
                // 참석 + 위치공유 ON + 네이티브 환경 → 네이티브 스케줄 등록
                if (status === 'ATTENDING' && locationSharing && isNativeApp()) {
                    const start = meeting?.scheduleStartDateTime || meeting?.schedule?.startDatetime;
                    const end = meeting?.scheduleEndDateTime || meeting?.schedule?.endDatetime;
                    if (start) {
                        const startTs = new Date(start).getTime();
                        const endTs = end ? new Date(end).getTime() : startTs + 3 * 60 * 60 * 1000;
                        if (endTs > Date.now()) {
                            setNativeMeetingSchedule(meetingId, startTs, endTs, {
                                meetingLat: meeting?.latitude ?? meeting?.lat,
                                meetingLon: meeting?.longitude ?? meeting?.lng ?? meeting?.lon,
                            });
                        }
                    }
                }
            } catch (e) {
                console.error('참석 정보 저장 실패:', e);
            }
        }
        setShowAttendance(false);
    };

    const handleOpenApp = () => {
        // 딥링크로 앱 열기 시도
        window.location.href = `dokocrew://invitation/${inviteCode}`;

        // 2초 후에도 페이지가 보이면 스토어로 이동
        setTimeout(() => {
            handleInstallApp();
        }, 2000);
    };

    const handleInstallApp = () => {
        const isIOS = /iPhone|iPad/i.test(navigator.userAgent);
        if (isIOS) {
            window.location.href = 'https://apps.apple.com/app/dokocrew/id000000000';
        } else {
            window.location.href = 'https://play.google.com/store/apps/details?id=com.dokoiot.dokocrew';
        }
    };

    const handleGoToMeeting = () => {
        if (meetingId) {
            navigate(`/meeting/detail/${meetingId}`, { replace: true });
        } else {
            navigate('/', { replace: true });
        }
    };

    return (
        <AppScreen bar="none">
            <header className={styles.hero}>
                <span className={styles.heroIcon}><IcParty size={40} /></span>
                <h1 className={styles.heroTitle}>{t('invitation.joinComplete')}</h1>
                <p className={styles.heroDesc}>
                    <strong>{meeting?.meetingName || t('meeting.detail')}</strong>{t('invitation.joinedTo')}
                </p>
            </header>

            {inApp ? (
                /* 앱 안: 모임 상세로 이동 */
                <div className={styles.actions}>
                    <Button variant="primary" size="lg" onClick={handleGoToMeeting}>
                        {t('invitation.viewMeetingDetail')}
                    </Button>
                    <Button variant="secondary" size="lg" onClick={() => navigate('/', { replace: true })}>
                        {t('invitation.goHome')}
                    </Button>
                </div>
            ) : (
                /* 브라우저: 앱 설치 유도 */
                <div className={styles.actions}>
                    <div className={styles.promo}>
                        <span className={styles.promoIcon}><IcPhone size={22} /></span>
                        <span className={styles.promoBody}>
                            <strong className={styles.promoTitle}>{t('invitation.appPromo')}</strong>
                            <p className={styles.promoDesc}>{t('invitation.appPromoDesc')}</p>
                        </span>
                    </div>

                    <Button variant="primary" size="lg" onClick={handleOpenApp}>
                        {t('invitation.openInApp')}
                    </Button>
                    <Button variant="secondary" size="lg" onClick={handleInstallApp}>
                        {t('invitation.installApp')}
                    </Button>
                </div>
            )}

            <AttendancePrompt
                visible={showAttendance && !!meetingId}
                meetingId={meetingId}
                meetingName={meeting?.meetingName || t('meeting.detail')}
                initialStatus="ATTENDING"
                onSubmit={handleAttendanceSubmit}
                onDismiss={() => setShowAttendance(false)}
            />
        </AppScreen>
    );
};
